import { RankingEntry } from '../../../shared/types/game.types';
import { apiGet } from '../../../utils/apiClient';
import {
  ServerRankingData,
  transformRankingData,
} from '../../../utils/rankingUtils';

// 페이지네이션 지원을 위한 캐시 구조 변경
interface CacheData {
  data: RankingEntry[];
  total: number;
  asOf?: string;
  timestamp: number;
}
const rankingCache = new Map<string, CacheData>();
const CACHE_DURATION = 1 * 60 * 1000; // 1분

export interface FetchRankingsResult {
  rankings: RankingEntry[];
  total: number;
  asOf?: string;
}

/**
 * 랭킹 데이터를 서버에서 가져옴 (캐싱 적용)
 */
export const fetchRankings = async (
  apiBaseUrl: string,
  page = 1,
  limit = 50,
  forceRefresh = false
): Promise<FetchRankingsResult> => {
  const cacheKey = `${page}_${limit}`;

  // 캐시 확인
  if (!forceRefresh && rankingCache.has(cacheKey)) {
    const cached = rankingCache.get(cacheKey)!;
    if (Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(
        `✅ 클라이언트 캐시에서 랭킹 반환 (page: ${page}, limit: ${limit})`
      );
      return { rankings: cached.data, total: cached.total, asOf: cached.asOf };
    }
  }

  console.log(`🌐 서버에서 랭킹 데이터 조회 (page: ${page}, limit: ${limit})`);

  const serverData = await apiGet<any>(
    `${apiBaseUrl}/api/user/rankings?page=${page}&limit=${limit}`
  );

  console.log('📥 서버 응답 원본:', {
    serverDataType: typeof serverData,
    isArray: Array.isArray(serverData),
    hasData:
      serverData && typeof serverData === 'object' && 'data' in serverData,
    keys:
      serverData && typeof serverData === 'object'
        ? Object.keys(serverData)
        : [],
    firstDataItem: serverData?.data?.[0] || serverData?.[0],
  });

  // 데이터 파싱 로직
  let rankingsArray: any[] = [];
  let total = 0;
  let asOf: string | undefined = undefined;

  if (serverData && typeof serverData === 'object') {
    // ✅ 최신 응답 형태: { rankings: [...], total, asOf, page, limit, totalPages }
    if (
      'rankings' in serverData &&
      Array.isArray((serverData as any).rankings)
    ) {
      rankingsArray = (serverData as any).rankings;
      total = Number((serverData as any).total ?? rankingsArray.length);
      asOf =
        typeof (serverData as any).asOf === 'string'
          ? (serverData as any).asOf
          : undefined;
    } else
    // 백엔드가 { data: [...], meta: { total: ... } } 형태로 응답한다고 가정
    // 하지만 현재 apiGet은 response.data를 반환하므로
    // UserController의 응답 구조에 따라 data 필드 안에 실제 배열이 있고, meta 필드가 있을 수 있음
    // UserController 수정본을 보면: { success, message, data: [...], meta: { total, ... } }
    // ResponseInterceptor가 data 필드만 추출해서 줄 경우 meta 정보가 누락될 수 있음.
    // 따라서 apiGet의 리턴값을 확인해야 함.
    // 여기서는 apiGet이 인터셉터를 거친 결과를 준다고 가정하고,
    // 만약 인터셉터가 'data' 필드만 준다면 meta 정보를 받을 수 없음.
    // => apiGet 타입을 any로 해서 전체 응답을 받거나, 인터셉터 로직을 확인해야 함.

    // 일단 일반적인 NestJS + Axios 패턴에서 data 프로퍼티를 사용한다고 가정
    if (Array.isArray(serverData)) {
      rankingsArray = serverData;
      total = serverData.length; // 메타 정보가 없으면 길이를 사용
    } else if ('data' in serverData && Array.isArray(serverData.data)) {
      rankingsArray = serverData.data;
      // meta 정보가 있으면 사용
      if ('meta' in serverData && typeof serverData.meta === 'object') {
        total = serverData.meta.total || rankingsArray.length;
        asOf =
          typeof (serverData as any).asOf === 'string'
            ? (serverData as any).asOf
            : undefined;
      } else {
        // meta가 없지만 전체 갯수를 추정할 수 없는 경우 현재 페이지 갯수로 fallback
        total = rankingsArray.length;
      }
    } else {
      // apiGet이 이미 data를 추출해서 준 경우 (ResponseInterceptor 동작 방식에 따라 다름)
      // 만약 apiGet이 response.data.data를 리턴한다면 rankingsArray가 됨
      // 하지만 meta 정보가 필요하므로 apiGet 호출 시 구조 확인이 필요함.
      // 현재로서는 안전하게 처리
      if (Array.isArray(serverData)) {
        rankingsArray = serverData;
        total = serverData.length;
      }
    }
  }

  // 랭킹 데이터가 없으면 빈 배열 반환
  if (!rankingsArray) {
    return { rankings: [], total: 0 };
  }

  // 서버 데이터를 클라이언트 형식으로 변환
  console.log('📊 랭킹 데이터 변환 전:', {
    rankingsArrayLength: rankingsArray.length,
    firstItem: rankingsArray[0],
    firstItemKeys: rankingsArray[0] ? Object.keys(rankingsArray[0]) : [],
    firstItemIsPublicProfile: rankingsArray[0]?.isPublicProfile,
  });

  const serverRankingData: ServerRankingData[] = rankingsArray.map(
    (item: any) => {
      const result = {
        rank: Number(item?.rank ?? 0),
        userKey: Number(item?.userKey ?? 0),
        nickname: item?.nickname || '익명',
        balance: Number(item?.balance ?? 0),
        gameCount: Number(item?.gameCount ?? 0),
        updatedAt: item?.updatedAt || '',
        isPublicProfile: item?.isPublicProfile ?? false,
        selectedTitle: item?.selectedTitle
          ? {
              titleId: item.selectedTitle.titleId,
              name: item.selectedTitle.name,
              icon: item.selectedTitle.icon || '',
              rarity:
                (item.selectedTitle.rarity as
                  | 'common'
                  | 'rare'
                  | 'epic'
                  | 'legendary') || 'common',
            }
          : null,
      };
      // 이메일 로그 제거 (민감 정보 보호)
      return result;
    }
  );

  // 클라이언트에서 수익률과 배지 계산
  const clientRankingData = transformRankingData(serverRankingData);

  // ClientRankingData를 RankingEntry로 변환
  const rankingList: RankingEntry[] = clientRankingData.map(item => ({
    ...item,
    selectedTitle: item.selectedTitle || null,
  }));

  // 캐시에 저장
  rankingCache.set(cacheKey, {
    data: rankingList,
    total: total || rankingList.length, // total이 0이면 현재 리스트 길이로
    asOf,
    timestamp: Date.now(),
  });
  console.log('💾 랭킹 데이터를 클라이언트 캐시에 저장');

  return { rankings: rankingList, total: total || rankingList.length, asOf };
};
